import { featureCollection } from '@turf/turf';

import type { GpAssignmentMapPoint } from '../../data-access/types/gp-sampling-assignment';
import type { AssignmentMapColorMode } from '../../lib/assignment-context';

import type { NgpPeriodHighlight } from './point-colors';
import {
  ngpPeriodHighlight,
  laboratoryFillColor,
  periodFillColor,
  pointFillColor,
  pointFillOpacity,
  pointStrokeColor,
  pointStrokeWidth,
  samplerFillColor,
  samplingFillColor,
} from './point-colors';

export const ASSIGNMENT_POINTS_SOURCE_ID = 'gp-assignment-points';
export const ASSIGNMENT_POINTS_LAYER_ID = 'gp-assignment-points-circle';

const DENSITY_TWIN_OFFSET_METERS = 12;

function usesRingStroke(point: GpAssignmentMapPoint, isNgp: boolean): boolean {
  return isNgp && (point.analysis_type === 'granulo' || point.analysis_type === 'density');
}

function displayGeometry(point: GpAssignmentMapPoint, isNgp: boolean): GeoJSON.Point {
  if (
    !isNgp
    || point.analysis_type !== 'density'
    || point.twin_analysis_type !== 'carbon'
  ) {
    return point.geometry;
  }
  const [lng, lat] = point.geometry.coordinates;
  const deltaLng = DENSITY_TWIN_OFFSET_METERS / (111320 * Math.cos((lat * Math.PI) / 180));
  return {
    type: 'Point',
    coordinates: [lng + deltaLng, lat],
  };
}

export function buildPointsLookup(
  points: GpAssignmentMapPoint[],
): Map<number, GpAssignmentMapPoint> {
  const lookup = new Map<number, GpAssignmentMapPoint>();
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    lookup.set(p.id, p);
  }
  return lookup;
}

interface BuildGeoJsonOptions {
  isNgp: boolean;
  colorMode: AssignmentMapColorMode;
  legendPeriod: string;
  periodHighlightMode: 'sms' | 'sampler';
  samplerLegendMode?: 'organization' | 'user';
}

function resolveFillColor(
  point: GpAssignmentMapPoint,
  colorMode: AssignmentMapColorMode,
  highlight: NgpPeriodHighlight,
  isNgp: boolean,
  legendPeriod: string,
  samplerLegendMode: 'organization' | 'user',
): string {
  switch (colorMode) {
    case 'sampler':
      return samplerFillColor(point, samplerLegendMode);
    case 'laboratory':
      return laboratoryFillColor(point);
    case 'period':
      return periodFillColor(
        point,
        legendPeriod as 'spring' | 'summer' | 'autumn',
        isNgp,
        highlight,
      );
    case 'sampling':
      return samplingFillColor(point.status);
    default:
      return pointFillColor(point.organic_status);
  }
}

function resolveHighlight(
  point: GpAssignmentMapPoint,
  colorMode: AssignmentMapColorMode,
  isNgp: boolean,
  legendPeriod: string,
  periodHighlightMode: 'sms' | 'sampler',
): NgpPeriodHighlight {
  if (colorMode !== 'period' || !isNgp) {
    return 'neutral';
  }
  return ngpPeriodHighlight(
    point,
    legendPeriod as 'spring' | 'summer' | 'autumn',
    periodHighlightMode,
  );
}

export function buildAssignmentPointsGeoJson(
  points: GpAssignmentMapPoint[],
  {
    isNgp,
    colorMode,
    legendPeriod,
    periodHighlightMode,
    samplerLegendMode = 'user',
  }: BuildGeoJsonOptions,
) {
  const features = new Array(points.length);

  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    const highlight = resolveHighlight(
      p,
      colorMode,
      isNgp,
      legendPeriod,
      periodHighlightMode,
    );
    const periodStroke = colorMode === 'period' && isNgp;
    const ring = usesRingStroke(p, isNgp);

    features[i] = {
      type: 'Feature' as const,
      id: p.id,
      geometry: displayGeometry(p, isNgp),
      properties: {
        id: p.id,
        fillColor: resolveFillColor(
          p,
          colorMode,
          highlight,
          isNgp,
          legendPeriod,
          samplerLegendMode,
        ),
        fillOpacity: colorMode === 'period' ? pointFillOpacity(highlight, isNgp, p.visible_to_sampler) : 1,
        strokeColor: periodStroke
          ? pointStrokeColor(p, highlight, isNgp)
          : ring
            ? '#212529'
            : '#ffffff',
        strokeWidth: periodStroke
          ? pointStrokeWidth(p, highlight, isNgp) + (ring ? 1 : 0)
          : ring
            ? 2
            : 1,
      },
    };
  }

  return featureCollection(features);
}

export function resolveClickedPointId(
  feature: GeoJSON.Feature | undefined,
): number | null {
  if (!feature) return null;

  const fromId = feature.id;
  if (typeof fromId === 'number' && Number.isFinite(fromId)) {
    return fromId;
  }

  const raw = feature.properties?.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.length > 0) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}
